import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useRef } from 'react';
import TextAreaAutosize from 'react-autosize-textarea';

import styles from './styles.module.scss';
import Icon from '../Icon/index.ts';
import addVariants from '../utils/addVariants/index.ts';
import useForkRef from '../utils/useForkRef/index.ts';

export const STYLES = ['outline', 'underline', 'no-border', 'fill', 'dense', 'text-large'];
export const COLORS = ['light', 'white', 'transparent'];
export const VARIANTS = COLORS.concat(STYLES).concat(['default']);

const TextArea = TextAreaAutosize;
const noop = () => 0;
const Input = React.forwardRef(
  (
    {
      alignIcon,
      className,
      color,
      description,
      isDisabled,
      error,
      hasError,
      hint,
      isFocused,
      icon,
      label,
      onBlur,
      onChange,
      onFocus,
      placeholder,
      renderError,
      isRequired,
      shouldShowDescription,
      shouldShowErrorMessage,
      shouldShowHint,
      style,
      type,
      unit,
      value,
      variant,
      isMultiline,
      maxRows,
      minRows,
      clearButton,
      isClearable,
      shouldAutoFocus,
      shouldUseLabelAsPlaceholder,
      ...props
    },
    ref
  ) => {
    const hasValue = Boolean(value);
    const inputRef = useRef();
    const sharedRef = useForkRef(ref, inputRef);
    const focusOnInput = useCallback(
      () => !isDisabled && inputRef.current.focus(),
      [isDisabled, inputRef]
    );
    const message = (hasError && renderError(error)) || hint;
    let footerEl;
    let labelEl;
    let descriptionEl;
    let unitEl;
    let placeholderEl;
    let iconEl;

    if ((shouldShowErrorMessage && hasError && message) || (shouldShowHint && message)) {
      footerEl = (
        <div
          className={classnames(
            addVariants(
              'TextInput__footer',
              styles,
              isMultiline ? 'multi-line' : 'single-line',
              hasError ? 'error' : 'valid'
            )
          )}
        >
          {message}
        </div>
      );
    }

    if (label) {
      labelEl = (
        <div
          className={classnames(
            addVariants(
              'TextInput__header__label',
              styles,
              variant,
              hasValue ? 'filled' : 'empty',
              hasError ? 'error' : 'valid',
              isFocused ? 'focused' : 'blurred',
              placeholder ? 'has-placeholder' : 'no-placeholder'
            )
          )}
        >
          {placeholder || isFocused || hasValue || !shouldUseLabelAsPlaceholder ? (
            label
          ) : (
            <span>&nbsp;</span>
          )}
          {isRequired && (
            <span
              className={addVariants(
                'TextInput__header__label__required-indicator',
                styles,
                hasError ? 'error' : 'valid'
              )}
            >
              &nbsp;*
            </span>
          )}
        </div>
      );
    }

    if (shouldShowDescription && description) {
      descriptionEl = (
        <div
          className={classnames(
            addVariants('TextInput__header__description', styles, hasError ? 'error' : 'valid')
          )}
        >
          {description}
        </div>
      );
    }

    if (unit) {
      unitEl = (
        // eslint-disable-next-line jsx-a11y/interactive-supports-focus
        <div
          className={classnames(
            addVariants(
              'TextInput__body__unit',
              styles,
              variant,
              color,
              hasError ? 'error' : 'valid',
              isFocused ? 'focused' : 'blurred',
              unit ? 'has-units' : 'no-units',
              icon ? 'has-icon' : 'no-icon'
            )
          )}
          onClick={focusOnInput}
          role="button"
        >
          {unit}
        </div>
      );
    }

    if (icon) {
      iconEl = (
        <Icon
          icon={icon}
          onClick={focusOnInput}
          className={classnames(
            addVariants(
              'TextInput__body__main__icon',
              styles,
              hasError ? 'error' : 'valid',
              alignIcon
            )
          )}
        />
      );
    }

    if (!isFocused && (placeholder || (label && shouldUseLabelAsPlaceholder))) {
      placeholderEl = (
        // eslint-disable-next-line jsx-a11y/interactive-supports-focus
        <div
          className={classnames(
            addVariants(
              'TextInput__body__main__placeholder__content',
              styles,
              hasValue ? 'filled' : 'empty',
              icon ? 'has-icon' : 'no-icon'
            )
          )}
          onClick={focusOnInput}
          role="button"
        >
          {placeholder || label}
        </div>
      );
    }

    const InputComponent = isMultiline ? TextArea : 'input';
    const textAreaProps = {
      maxRows
    };
    if (minRows) {
      // react-autosize-textarea does not support minRows
      // So we simulate it by assuming a line height of 16
      textAreaProps.style = {
        minHeight: `${(minRows + 1) * 16}px`
      };
    }

    const hasHeader = Boolean(labelEl || descriptionEl);

    return (
      <div
        className={classnames(addVariants('TextInput', styles, color, variant), className)}
        style={style}
      >
        {hasHeader && (
          <div className={addVariants('TextInput__header', styles)}>
            {labelEl}
            {descriptionEl}
          </div>
        )}
        <div
          className={classnames(
            addVariants(
              'TextInput__body',
              styles,
              hasHeader ? 'has-header' : 'no-header',
              color,
              variant
            )
          )}
        >
          <div className={styles.TextInput__body__main}>
            {iconEl}
            <div className={styles.TextInput__body__main__placeholder}>{placeholderEl}</div>
            <InputComponent
              type={type}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              onFocus={onFocus}
              className={classnames(
                addVariants(
                  'TextInput__body__main__input',
                  styles,
                  variant,
                  color,
                  hasError ? 'error' : 'valid',
                  isDisabled ? 'disabled' : 'enabled',
                  isFocused ? 'focused' : 'blurred',
                  unit ? 'has-units' : 'no-units',
                  icon ? 'has-icon' : 'no-icon',
                  isClearable ? 'has-clear-button' : 'no-clear-button'
                )
              )}
              data-error={hasError}
              disabled={isDisabled}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={shouldAutoFocus || isFocused}
              ref={sharedRef}
              {...(isMultiline ? textAreaProps : {})}
              {...props}
            />
            {clearButton}
          </div>
          {unitEl}
        </div>
        {footerEl}
      </div>
    );
  }
);

Input.propTypes = {
  /**
   * The type of input
   */
  type: PropTypes.oneOf(['text', 'password', 'email', 'hidden', 'number', 'search']),
  /**
   * Whether the input is disabled
   */
  isDisabled: PropTypes.bool,
  /**
   * The placeholder to show before the input has a value
   */
  placeholder: PropTypes.string,
  /**
   * The current value of the input, state must be handled by the caller
   */
  value: PropTypes.string,
  /**
   * Whether the input has an error
   */
  hasError: PropTypes.bool,
  /**
   * Whether the input is focused
   */
  isFocused: PropTypes.bool,
  /**
   * Whether the should automatically focus
   */
  shouldAutoFocus: PropTypes.bool,
  /**
   * The current state of the error, state must be handled by the caller
   */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.node, PropTypes.oneOf([null])]),
  /**
   * Whether the input is required
   */
  isRequired: PropTypes.bool,
  /**
   * The rules the input must follow to be considered valid
   */
  rules: PropTypes.arrayOf(PropTypes.func),
  /**
   * The label for the input
   */
  label: PropTypes.oneOfType([PropTypes.node, PropTypes.func, PropTypes.bool]),
  /**
   * A second line below the input
   */
  description: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  /**
   * Whether to show the description
   */
  shouldShowDescription: PropTypes.bool,
  /**
   * Hint text below the input
   */
  hint: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  /**
   * Whether to show the hint
   */
  shouldShowHint: PropTypes.bool,
  /**
   * Whether to show the error message
   */
  shouldShowErrorMessage: PropTypes.bool,
  /**
   * Decides the variant of the button
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  color: PropTypes.oneOfType([
    PropTypes.oneOf(COLORS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(COLORS), PropTypes.bool]))
  ]),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * On change of the input's value
   */
  onChange: PropTypes.func,
  /**
   * Formats the value of the error returned from the validation rules
   */
  renderError: PropTypes.func,
  /**
   * If true, when only provided a label, the label will act
   * as a placeholder and once the input field is focused the label
   * jumps to above the input
   */
  shouldUseLabelAsPlaceholder: PropTypes.bool,
  /**
   * The unit that input is in to display next to the input
   */
  unit: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**
   * Icon to place on left side
   */
  icon: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string.isRequired),
    PropTypes.shape({
      iconName: PropTypes.string.isRequired,
      prefix: PropTypes.string.isRequired
    })
  ]),
  /**
   * Align the icon to the: right, left
   */
  alignIcon: PropTypes.oneOf(['left', 'right']),
  /**
   * Whether the input is rendered onto a textarea
   */
  isMultiline: PropTypes.bool,
  isClearable: PropTypes.bool,
  maxRows: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([undefined])]),
  minRows: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([undefined])]),
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  clearButton: PropTypes.node,
  style: PropTypes.shape({})
};

Input.defaultProps = {
  alignIcon: 'left',
  className: '',
  clearButton: null,
  color: 'light',
  description: false,
  isDisabled: false,
  error: null,
  hasError: false,
  isFocused: false,
  shouldAutoFocus: false,
  hint: false,
  icon: null,
  label: false,
  maxRows: undefined,
  minRows: undefined,
  isMultiline: false,
  isClearable: false,
  shouldUseLabelAsPlaceholder: false,
  onBlur: noop,
  onChange: noop,
  onFocus: noop,
  placeholder: '',
  renderError: errors => (Array.isArray(errors) ? errors.join(' & ') : errors),
  isRequired: false,
  rules: [],
  shouldShowDescription: true,
  shouldShowErrorMessage: true,
  shouldShowHint: true,
  style: {},
  type: 'text',
  unit: false,
  value: null,
  variant: ['outline', 'light']
};

export default Input;
